import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Eye, BellOff, Radio } from "lucide-react";
import InsightPanel from "@/components/InsightPanel";

const fmt = (v, suffix = "") => (v == null ? "—" : `${v}${suffix}`);
const fmtMoney = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
};

function DataRow({ label, value, mono = true }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0">
      <span className="overline text-gray-400">{label}</span>
      <span className={mono ? "metric-value text-sm" : "text-sm"}>{value}</span>
    </div>
  );
}

export default function DealDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [deal, setDeal] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const toastShownRef = useRef(false);

  const runAnalysis = async (dealData) => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/analyze-auto", {
        deal_id: dealData.id,
        deal: dealData,
      });
      setAnalysis(data);
      await api.post("/alert-logs", {
        deal_id: dealData.id,
        analysis_id: data.id,
        action: "shown",
      });
      // Fire toast conditionally
      if (!toastShownRef.current && !data.snoozed && (data.risk_level === "HIGH" || data.risk_level === "MEDIUM")) {
        toastShownRef.current = true;
        const isHigh = data.risk_level === "HIGH";
        toast.custom(
          (t) => (
            <div
              data-testid={`toast-${data.risk_level.toLowerCase()}`}
              className="bg-white border rounded-sm p-4 w-[380px] shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
              style={{
                borderColor: isHigh ? "#DC2626" : "#EA580C",
                borderWidth: "1px",
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-1 self-stretch rounded-sm"
                  style={{ backgroundColor: isHigh ? "#DC2626" : "#EA580C" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`risk-pill ${data.risk_level.toLowerCase()}`}>
                      ! {data.risk_level} RISK
                    </span>
                    <span className="overline text-gray-400">iSale</span>
                  </div>
                  <div className="text-sm text-gray-900 leading-snug mb-3">
                    {data.narrative}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setPanelOpen(true);
                        toast.dismiss(t);
                      }}
                      data-testid="toast-view-btn"
                      className="px-3 py-1.5 bg-black text-white text-xs rounded-sm hover:bg-gray-800 flex items-center gap-1.5"
                    >
                      <Eye className="w-3 h-3" /> View Insights
                    </button>
                    <button
                      onClick={async () => {
                        await api.post("/alert-logs", {
                          deal_id: dealData.id,
                          analysis_id: data.id,
                          action: "snoozed",
                        });
                        toast.dismiss(t);
                        toast.success("Snoozed for 24 hours");
                      }}
                      data-testid="toast-snooze-btn"
                      className="px-3 py-1.5 border border-gray-200 text-gray-700 text-xs rounded-sm hover:bg-gray-50 flex items-center gap-1.5"
                    >
                      <BellOff className="w-3 h-3" /> Snooze 24h
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ),
          { duration: 12000 }
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    toastShownRef.current = false;
    (async () => {
      try {
        const { data } = await api.get(`/deals/${id}`);
        if (cancelled) return;
        setDeal(data);
        // simulate 1.5s extraction delay (as if Chrome ext detected page)
        setTimeout(() => {
          if (!cancelled) runAnalysis(data);
        }, 1500);
      } catch {
        toast.error("Deal not found");
        nav("/app");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!deal) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="text-sm text-gray-400">Loading deal...</div>
      </div>
    );
  }

  const risk = analysis?.risk_level;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="deal-detail-page">
      <Link
        to="/app"
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-6"
        data-testid="back-to-pipeline"
      >
        <ArrowLeft className="w-3 h-3" /> Back to pipeline
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-gray-200 bg-white rounded-sm p-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="overline text-gray-400">{deal.crm_source}</span>
              <span className="text-xs text-gray-300">·</span>
              <span className="font-mono text-xs text-gray-400">{deal.crm_id}</span>
            </div>
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">
              {deal.deal_name}
            </h1>
            <div className="text-base text-gray-500 mb-6">{deal.company}</div>

            <div className="grid grid-cols-2 gap-x-10">
              <div>
                <DataRow label="Deal Value" value={fmtMoney(deal.deal_value)} />
                <DataRow label="Stage" value={deal.stage} mono={false} />
                <DataRow label="Discount" value={fmt(deal.discount_pct, "%")} />
                <DataRow label="Close Date" value={fmtDate(deal.close_date)} />
              </div>
              <div>
                <DataRow label="Days in Stage" value={fmt(deal.days_in_stage)} />
                <DataRow label="Last Activity" value={fmtDate(deal.last_activity_date)} />
                <DataRow label="Meetings" value={fmt(deal.meeting_count)} />
                <DataRow label="Email Response Rate" value={
                  deal.email_response_rate != null
                    ? `${(deal.email_response_rate * 100).toFixed(0)}%`
                    : "—"
                } />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-gray-200 bg-white rounded-sm p-6" data-testid="analysis-sidecard">
            <div className="flex items-center gap-2 mb-6">
              <Radio className={`w-3.5 h-3.5 ${analyzing ? "scan-pulse text-[#DC2626]" : "text-gray-400"}`} />
              <span className="overline text-gray-400">
                {analyzing ? "iSale analyzing…" : "iSale analysis"}
              </span>
            </div>

            {analyzing ? (
              <div className="space-y-3">
                <div className="h-16 bg-gray-100 rounded-sm scan-pulse" />
                <div className="h-3 bg-gray-100 rounded-sm scan-pulse w-3/4" />
                <div className="h-3 bg-gray-100 rounded-sm scan-pulse w-1/2" />
              </div>
            ) : analysis ? (
              <>
                <div className="mb-6">
                  <div className="overline text-gray-400 mb-2">Risk Level</div>
                  <span className={`risk-pill ${risk?.toLowerCase()}`} data-testid="sidecard-risk">
                    {risk}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="border-l-2 border-black pl-3">
                    <div className="overline text-gray-400 mb-1">Reality</div>
                    <div className="metric-value text-3xl">
                      {analysis.reality_score?.toFixed(1)}
                    </div>
                  </div>
                  <div className="border-l-2 border-gray-300 pl-3">
                    <div className="overline text-gray-400 mb-1">Gap</div>
                    <div className="metric-value text-3xl">
                      {analysis.contradiction_gap >= 0 ? "+" : ""}
                      {analysis.contradiction_gap?.toFixed(1)}
                    </div>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-gray-900 mb-6" data-testid="sidecard-narrative">
                  {analysis.narrative}
                </p>
                <Button
                  onClick={() => setPanelOpen(true)}
                  className="w-full rounded-sm bg-black hover:bg-gray-800 gap-2"
                  data-testid="open-insights-btn"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Open full insights
                </Button>
              </>
            ) : (
              <div className="text-sm text-gray-400">No analysis yet.</div>
            )}
          </div>
        </div>
      </div>

      <InsightPanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        analysis={analysis}
        deal={deal}
      />
    </div>
  );
}
